import { Home, Building2, LandPlot, MapPin, Maximize2, BedDouble, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { resolveApiAssetUrl } from '@/lib/api';

const typeIcons = {
  haus: Home,
  wohnung: Building2,
  grundstueck: LandPlot,
};

const typeColors = {
  haus: 'bg-[#0f9d94]/15 text-[#0f9d94] border-[#0f9d94]/20',
  wohnung: 'bg-violet-100 text-violet-700 border-violet-200',
  grundstueck: 'bg-[#8b5a2b]/15 text-[#8b5a2b] border-[#8b5a2b]/20',
};

const typeLabels = {
  haus: 'Haus',
  wohnung: 'Wohnung',
  grundstueck: 'Grundstück',
};

export const PropertyCard = ({ property, onViewDetails, compact = false }) => {
  const Icon = typeIcons[property.property_type] || MapPin;
  const colorClass = property.is_active === false
    ? 'bg-slate-100 text-slate-600 border-slate-200'
    : (typeColors[property.property_type] || typeColors.grundstueck);
  const typeLabel = typeLabels[property.property_type] || property.property_type;
  const addressParts = [
    [property.street, property.street_number].filter(Boolean).join(' ').trim(),
    [property.postcode, property.city].filter(Boolean).join(' ').trim(),
  ].filter(Boolean);
  const compactAddress = addressParts.join(', ') || property.city || property.postcode || 'Standort ansehen';

  if (compact) {
    return (
      <div
        className={`w-[min(78vw,240px)] p-3 sm:min-w-[240px] sm:p-4 ${property.is_active === false ? 'opacity-80' : ''}`}
        data-testid={`property-card-${property.property_id}`}
      >
        {property.images?.[0] && (
          <div className="mb-3 h-24 w-full overflow-hidden rounded-lg sm:h-28">
            <img
              src={resolveApiAssetUrl(property.images[0])}
              alt={property.title}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        <Badge variant="secondary" className={`${colorClass} mb-2`}>
          <Icon className="mr-1 h-3 w-3" />
          {typeLabel}
        </Badge>
        {property.is_active === false && (
          <Badge variant="secondary" className="mb-2 ml-2 border-slate-300 bg-slate-200 text-slate-700">
            Inaktiv
          </Badge>
        )}

        <h3 className="mb-2 line-clamp-2 text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          {property.title}
        </h3>

        <div className="mb-3 flex items-start gap-1 text-xs text-stone-500">
          <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="line-clamp-2">{compactAddress}</span>
        </div>

        {property.responsible_broker && (
          <div className="mb-3 rounded-md border border-stone-200 bg-stone-50 px-2.5 py-2 text-xs text-slate-700">
            <div className="flex items-start gap-1.5">
              <UserRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Betreuung</p>
                <p className="truncate font-semibold text-slate-900">{property.responsible_broker}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mb-3 flex items-center gap-3 text-xs text-stone-600">
          {property.rooms && (
            <div className="flex items-center gap-1">
              <BedDouble className="h-3 w-3" />
              <span>{property.rooms} Zi.</span>
            </div>
          )}
          {property.living_area && (
            <div className="flex items-center gap-1">
              <Maximize2 className="h-3 w-3" />
              <span>{property.living_area} m²</span>
            </div>
          )}
        </div>

        <div className="flex items-end justify-between gap-3">
          {property.price ? (
            <div className="min-w-0 flex items-center gap-1 font-bold text-slate-900">
              <span className="truncate">{property.price.toLocaleString('de-DE')} €</span>
            </div>
          ) : (
            <span className="text-sm text-stone-400">Preis auf Anfrage</span>
          )}

          <Button
            data-testid={`view-details-${property.property_id}`}
            size="sm"
            onClick={onViewDetails}
            className="h-7 px-3 text-xs bg-slate-900 hover:bg-slate-800"
          >
            Vollprofil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`overflow-hidden rounded-lg bg-white shadow-lg ${property.is_active === false ? 'opacity-80' : ''}`} data-testid={`property-card-full-${property.property_id}`}>
      {property.images?.[0] && (
        <div className="h-48 w-full overflow-hidden">
          <img
            src={resolveApiAssetUrl(property.images[0])}
            alt={property.title}
            className="h-full w-full object-cover"
          />
        </div>
      )}
      <div className="p-6">
        <Badge variant="secondary" className={`${colorClass} mb-3`}>
          <Icon className="mr-1 h-3 w-3" />
          {typeLabel}
        </Badge>
        {property.is_active === false && (
          <Badge variant="secondary" className="mb-3 ml-2 border-slate-300 bg-slate-200 text-slate-700">
            Inaktiv
          </Badge>
        )}
        <h3 className="heading-md mb-2 text-slate-900">{property.title}</h3>
        <div className="mb-4 flex items-center gap-2 text-stone-500">
          <MapPin className="h-4 w-4" />
          <span>{property.city || 'Standort verfügbar'}</span>
        </div>
        {property.price && (
          <div className="mb-4 text-2xl font-bold text-slate-900">
            {property.price.toLocaleString('de-DE')} €
          </div>
        )}
        <Button onClick={onViewDetails} className="w-full bg-slate-900 hover:bg-slate-800">
          Vollständiges Profil
        </Button>
      </div>
    </div>
  );
};

export default PropertyCard;
